import BoostLogo from "@/assets/svg/full-logo/boost-white.svg";
import PayLogo from "@/assets/svg/full-logo/pay-white.svg";
import ShieldLogo from "@/assets/svg/full-logo/shield-white.svg";
import TaskLogo from "@/assets/svg/full-logo/task.svg";
import {
  Call,
  Mail,
  Support,
  Telegram,
  Whatsapp,
} from "@/components/SVGSocial";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import scrollTo from "@/utils/scrollTo";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SupportTicket from "../SupportTicket";

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const landingPage = import.meta.env.VITE_LANDING_PAGE;

  const [modalOpen, setModalOpen] = useState(false);

  let logo;
  let dashboard;

  switch (landingPage) {
    case "shield":
      logo = ShieldLogo;
      dashboard = "EnfiniShield";
      break;
    case "pay":
      logo = PayLogo;
      dashboard = "EnfiniPay";
      break;
    case "boost":
      logo = BoostLogo;
      dashboard = "EnfiniBoost";
      break;
    case "task":
      logo = TaskLogo;
      dashboard = "EnfiniTask";
      break;
    default:
      logo = ShieldLogo;
      dashboard = "EnfiniShield";
      break;
  }

  const platforms = [
    {
      icon: Telegram,
      url: "https://web.telegram.org/a/#6110481522",
    },
    { icon: Support, action: () => setModalOpen(true) },
    {
      icon: Whatsapp,
      url: "https://api.whatsapp.com/send/?phone=639291514853",
    },
    { icon: Call, tooltip: "+63 929 151 4853" },
    {
      icon: Mail,
      url: "https://mail.google.com/mail/u/0/?fs=1&to=requests@enfinity.com&tf=cm",
    },
  ];

  const goUp = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollTo(location.state.scrollTo);
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);
    }
  }, [location, navigate]);

  const handleScrollTo = (targetPath, scrollId) => {
    if (location.pathname === targetPath) {
      scrollTo(scrollId);
    } else {
      navigate(targetPath, { state: { scrollTo: scrollId } });
    }
  };

  return (
    <>
      <div className="bg-[linear-gradient(to_right,var(--primary),var(--tertiary))]">
        <div className="p-12 gap-10 text-white space-y-8 5xl:max-w-[100rem] 5xl:mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-12 lg:gap-0">
            <div className="flex flex-col gap-4 justify-center items-center text-center lg:hidden">
              <div className="items-center gap-2 flex">
                <img src={logo} className="w-40" alt="Logo" />
                <span>© 2025</span>
              </div>
              <p>
                You're using a beta version of{" "}
                <strong
                  className={`${
                    landingPage === "boost" ? "text-primary" : "text-white"
                  }`}
                >
                  {dashboard}
                </strong>
                .
              </p>
            </div>
            <div className="space-y-20 order-2 lg:order-1 mb-auto text-nowrap">
              <div className="space-y-4 hidden lg:block">
                <div className="items-center gap-2 flex">
                  <img src={logo} className="w-40" alt="Logo" />
                  <span>© 2025</span>
                </div>
                <p>
                  You're using a beta version of{" "}
                  <strong
                    className={`${
                      landingPage === "boost" ? "text-primary" : "text-white"
                    }`}
                  >
                    {dashboard}
                  </strong>
                  .
                </p>
              </div>
              <div className="flex flex-col gap-2 items-center lg:items-start">
                <h1 className="font-medium">Social Links:</h1>
                <div className="flex gap-2 items-center py-2">
                  {platforms.map((social, index) =>
                    social.url ? (
                      <a href={social.url} target="_blank" key={index}>
                        <div className="rounded-md p-4 text-primary bg-white">
                          <social.icon color="var" className="h-5 w-5" />
                        </div>
                        <span className="sr-only">
                          Contact us via {social.name}
                        </span>
                      </a>
                    ) : social.tooltip ? (
                      <Tooltip key={index}>
                        <TooltipTrigger>
                          <div className="rounded-md p-4 text-primary bg-white">
                            <social.icon
                              color="text-primary"
                              className="h-5 w-5"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{social.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <div
                        key={index}
                        className="rounded-md p-4 text-primary bg-white"
                        onClick={social.action}
                      >
                        <social.icon color="text-primary" className="h-5 w-5" />
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-end gap-10 lg:gap-12 4xl:gap-32 cursor-default text-center lg:text-right order-1 lg:order-2">
              <div className="flex flex-col lg:flex-row gap-6 items-center justify-end text-lg">
                {location.pathname === "/" ? (
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() => goUp()}
                  >
                    Home
                  </p>
                ) : (
                  <Link to="/" className="block">
                    <p className="cursor-pointer hover:underline">Home</p>
                  </Link>
                )}
                <p
                  className="cursor-pointer hover:underline"
                  onClick={() => handleScrollTo("/", "features")}
                >
                  Features
                </p>
                <p
                  className="cursor-pointer hover:underline"
                  onClick={() => handleScrollTo("/", "how-it-works")}
                >
                  How it works
                </p>
                {landingPage === "boost" && (
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() => handleScrollTo("/", "partners")}
                  >
                    Partners
                  </p>
                )}
                {location.pathname === "/contact-us" ? (
                  <p
                    className="cursor-pointer hover:underline"
                    onClick={() => goUp()}
                  >
                    Contact Us
                  </p>
                ) : (
                  <Link to="/contact-us" className="block">
                    <p className="cursor-pointer hover:underline">Contact Us</p>
                  </Link>
                )}
              </div>
              <div className="flex items-start">
                <div className="space-y-5 lg:space-y-3 lg:w-48 1xl:w-60 4xl:w-fit ml-auto">
                  <h1 className="font-bold text-primary">Other Products</h1>
                  <a
                    href="https://enfinity.com"
                    target="_blank"
                    className="cursor-pointer block hover:underline"
                  >
                    Enfinity (main site)
                  </a>
                  {landingPage !== "pay" && (
                    <a
                      href="https://enfinipay.com"
                      target="_blank"
                      className="cursor-pointer block hover:underline"
                    >
                      Enfinipay
                    </a>
                  )}
                  {landingPage !== "shield" && (
                    <a
                      href="https://enfinishield.com"
                      target="_blank"
                      className="cursor-pointer block hover:underline"
                    >
                      Enfinishield
                    </a>
                  )}
                  {landingPage !== "boost" && (
                    <a
                      href="https://enfiniboost.com"
                      target="_blank"
                      className="cursor-pointer block hover:underline"
                    >
                      Enfiniboost
                    </a>
                  )}
                  {landingPage !== "task" && (
                    <a
                      href="https://enfinitask.com"
                      target="_blank"
                      className="cursor-pointer block hover:underline"
                    >
                      Enfinitask
                    </a>
                  )}
                </div>
                {landingPage === "pay" && (
                  <div className="space-y-5 lg:space-y-3 lg:w-48 1xl:w-60 4xl:w-fit ml-auto">
                    <h6 className="font-bold text-primary">Compare Friends</h6>
                    <div className="text-white space-y-3">
                      <p>MCPay (MediaCube)</p>
                      <p>MilX (air.io)</p>
                      <p>Freedom! Pay</p>
                      <p>Rhei / BBTV</p>
                      <p>YoolaPay by Yoola</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-0 items-center justify-between w-full text-lg">
            <span>All rights reserved.</span>
            {/* <div className="hidden lg:flex items-center gap-4">
              <span>Privacy Policy</span>
              <span>Terms of Use</span>
              <span>Cookie Policy</span>
            </div> */}
            <span>© 2025 Enfinity Limited</span>
          </div>
        </div>
      </div>

      <SupportTicket modalOpen={modalOpen} setModalOpen={setModalOpen} />
    </>
  );
}
